import React from 'react'

import CustomDialog from 'shared/components/CustomDialog'

import { useAddCustomerMutation, useDeleteCustomerMutation, useEditCustomerMutation } from 'shared/store/useCustomers'
import { useLocationsQuery } from 'shared/store/useLocations'
import { CustomerType } from 'shared/store/useCustomers/types'
import { useSearchParams } from 'react-router-dom'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import { AxiosError } from 'axios'
import { ModalDetailsType } from '../../types'
import DeleteModal from './components/DeleteModal'
import NewModal from './components/NewModal'
import ForceDeleteModal from './components/ForceDeleteModal'

interface Props {
   modalDetails: ModalDetailsType
   setModalDetails: React.Dispatch<React.SetStateAction<ModalDetailsType>>
   onClose: (withRedirect?: boolean, customerUuid?: string) => void
   setFirstCustomer: () => void
}

export default function Modal({ modalDetails, setModalDetails, onClose, setFirstCustomer }: Props) {
   const { mutate: deleteCustomer, isLoading: isDeletingCustomer } = useDeleteCustomerMutation()
   const { mutate: editCustomer, isLoading: isEditingCustomer } = useEditCustomerMutation()
   const { mutate: createCustomer, isLoading: isCreatingCustomer } = useAddCustomerMutation()
   const { data: locationData } = useLocationsQuery()
   const [searchParams] = useSearchParams()

   function handleEdit(values: CustomerType) {
      const activeLocation = locationData?.locations.find(
         (item) => item.locationSettings.uuid === locationData.mainLocationUuid
      )
      if (modalDetails.variant === 'NEW') {
         createCustomer(
            {
               ...values,
               productsMarkupSettings: {
                  thresholds: activeLocation!.markupSettings!.thresholds,
                  priceType: activeLocation!.markupSettings!.priceType,
               },
            },
            {
               onSuccess: (data) => {
                  if (!!data.data.uuid && !!searchParams.get('new')) {
                     onClose(true, data.data.uuid)
                  } else {
                     setFirstCustomer()
                     onClose(false)
                  }
               },
            }
         )
      } else {
         editCustomer(
            {
               payload: {
                  ...values,
               },
               customerUuid: values.uuid,
            },
            {
               onSuccess: () => onClose(false),
            }
         )
      }
   }

   function handleDelete() {
      if (modalDetails.customerDetails?.uuid) {
         deleteCustomer(
            { customerUuid: modalDetails.customerDetails.uuid },
            {
               onSuccess: () => {
                  setFirstCustomer()
                  onClose()
               },
               onError: (err) => {
                  if (isAxiosError<ErrorType>(err)) {
                     if (
                        err.response?.status === 400 &&
                        err.response?.data?.errors[0].code === 'errors.client.has-linked-cart-reflinks'
                     ) {
                        setModalDetails((prev) => ({ ...prev, variant: 'FORCE_DELETE' }))
                     }
                  }
               },
            }
         )
      }
   }

   function handleForceDelete() {
      if (modalDetails.customerDetails?.uuid) {
         deleteCustomer(
            { customerUuid: modalDetails.customerDetails.uuid, force: true },
            {
               onSuccess: () => {
                  setFirstCustomer()
                  onClose(false)
               },
            }
         )
      }
   }

   function renderContent() {
      switch (modalDetails.variant) {
         case 'FORCE_DELETE':
            return (
               <ForceDeleteModal
                  customerName={modalDetails.customerDetails?.displayName ?? ''}
                  isDeleting={isDeletingCustomer}
                  onClose={() => onClose(false)}
                  onDelete={handleForceDelete}
               />
            )
         case 'DELETE':
            return (
               <DeleteModal
                  customerName={modalDetails.customerDetails?.displayName ?? ''}
                  isDeleting={isDeletingCustomer}
                  onClose={() => onClose(false)}
                  onDelete={handleDelete}
               />
            )
         case 'EDIT':
            return (
               <NewModal
                  onClose={() => onClose(false)}
                  onSubmit={(values) => handleEdit(values)}
                  customerDetails={modalDetails.customerDetails!}
                  isSubmitting={isEditingCustomer}
               />
            )
         case 'NEW':
            return (
               <NewModal
                  onSubmit={(values) => handleEdit(values)}
                  isSubmitting={isCreatingCustomer}
                  onClose={() => onClose(false)}
               />
            )
         default:
            return null
      }
   }

   return (
      <CustomDialog
         maxWidth={modalDetails.variant === 'DELETE' ? undefined : 'lg'}
         isLoading={modalDetails.variant === undefined}
         deleteVariant={modalDetails.variant === 'DELETE' || modalDetails.variant === 'FORCE_DELETE'}
         open={modalDetails.open}
         disableClosing
         onClose={() => onClose(false)}
         fullWidth
         content={renderContent()}
      />
   )
}
